import { Flex, Icon, Img, Radio, RadioGroup, Text, Tabs, TabList, TabPanels, Tab, TabPanel } from "@chakra-ui/react";
import { DynamicTable, ColumnDefinition } from "@/components/DynamicTable";
import { CardReceimentData, CardTaxData } from "@/models";
import { formatCurrency, getApplicationName } from "@/utils";
import { InstallmentArray } from "../useAdvanceReceipt";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { usePreviewContext } from "@/contexts/PreviewContext";
import { useGetTaxBrands } from "@/hooks/useGetTaxBrands";
import { Alert, Tooltip } from "barte-design-system";

interface RadioGroupBodyProps {
  array: InstallmentArray;
  setCreditCardReceivement: (data: CardReceimentData) => void;
  creditCardReceivement: "RECURRENT" | "EARLY";
  setCreditCardTax: (data: CardTaxData) => void;
  creditCardTax: "EARLY_BUYER" | "EARLY_SELLER";
  anchoringState?: boolean;
  setAnchoringState?: (param: boolean) => void;
  creditCardType?: string;
  isPunctualCharge?: boolean;
  selectedInstallments: string;
  watch?: any;

}

export function RadioGroupBody({
  setCreditCardReceivement,
  creditCardReceivement,
  setCreditCardTax,
  watch,
  creditCardTax,
  array,
  isPunctualCharge,
  selectedInstallments
}: RadioGroupBodyProps) {
  const isLocalhost = window?.location?.hostname.includes("localhost")
  const applicationName = getApplicationName({ type: isLocalhost ? "localhost" : "application" });
  const { previewData, setPreviewData } = usePreviewContext();
  const [hasToogle, setHasToogle] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(true);
  const [taxForBrands, setTaxForBrands] = useState<any>(null);
  const istaxV2 = localStorage.getItem("isActiveForTax")
  const hasView = localStorage?.getItem("barte-features-list-user")?.includes("view_new_preview_payment_link");
  useEffect(() => {
    if (hasView) {
      setHasToogle(hasView)
    }

  }, [hasView])

  const taxV2 = localStorage.getItem("isActiveForTax")
  const hasMixInstallments = localStorage.getItem("hasMixInstallments")

  const value = watch("value")
  const { getTaxForBrands } = useGetTaxBrands()

  const isRequestingRef = useRef(false);
  const lastRequestRef = useRef<number | null>(null);

  const numericValue = useMemo(() => {
    if (!value) return 0;
    return Number(value.toString().replace('.', '').replace(',', '.'));
  }, [value]);

  const discountPercent = useMemo(() => {
    const discount = previewData?.creditcard?.discount;
    if (!discount) return 0;
    return Number(discount.replace("%", "")) / 100;
  }, [previewData?.creditcard?.discount]);

  const finalValue = useMemo(() => {
    return numericValue - (numericValue * discountPercent);
  }, [numericValue, discountPercent]);

  const installments = previewData?.creditcard?.installments

  const getTaxForBrandsData = useCallback(async () => {
    if (lastRequestRef.current === finalValue) {
      return;
    }

    if (isRequestingRef.current) {
      return;
    }

    isRequestingRef.current = true;
    lastRequestRef.current = finalValue;

    try {
      const res = await getTaxForBrands({ amount: finalValue || 0 })
      if (res?.data) {
        setTaxForBrands(res?.data)
      }
    } catch (error) {
      return error
    } finally {
      isRequestingRef.current = false;
    }
  }, [finalValue, getTaxForBrands]);

  useEffect(() => {
    if (!finalValue || finalValue <= 0) {
      return;
    }

    const timer = setTimeout(() => {
      getTaxForBrandsData()
    }, 1000);

    return () => clearTimeout(timer);
  }, [finalValue, getTaxForBrandsData])


  useEffect(() => {
    if (Number(selectedInstallments) > 12 && applicationName === "stanleyspay") {
      setCreditCardTax("EARLY_BUYER")
    }
  }, [selectedInstallments])


  useEffect(() => {
    setPreviewData({ ...previewData, creditCardTax: creditCardTax || "EARLY_BUYER" })

  }, [creditCardTax])


  useEffect(() => {
    setPreviewData({ ...previewData, arrayInstallments: array, creditCardTax: creditCardTax })
  }, [array])



  return (
    <>
      <RadioGroup
        onChange={setCreditCardReceivement}
        value={creditCardReceivement}
      >
        <Flex flexDir="column" minW="332px" w="70%">
          <Flex flexDir="column">
            <Flex align="center" gap=".3rem">
              <Text lineHeight="1.5rem" color="#000000" fontSize="12px">
                Recebimento
              </Text>
              <Tooltip
                placement="top"
                label="
                Você pode optar por receber parcelado recorrente
                conforme configurações de pagamento acima ou
                receber antecipado mesmo que seu cliente pague
                parcelado."
              >
                <span>
                  <Icon w=".8rem" color="gray.400" as={AiOutlineInfoCircle} />
                </span>
              </Tooltip>
            </Flex>


            <Flex
              flexDir="row"
              w="100%"
              gap="10px">
              <Flex
                onClick={() => setCreditCardReceivement("EARLY")}
                cursor="pointer"
                border={creditCardReceivement === "EARLY" ? "2px solid black" : "1px solid gray"}
                justifyContent="center"
                p="5px"
                w="124px"
                borderRadius="4px">
                <Text
                  fontSize="14px"
                  fontWeight={creditCardReceivement === "EARLY" ? "600" : "400"}
                  color={creditCardReceivement === "EARLY" ? "black" : "newGray.400"}
                >Antecipado</Text>
              </Flex>


              <Flex

                onClick={() => setCreditCardReceivement("RECURRENT")}
                cursor="pointer"
                border={creditCardReceivement === "RECURRENT" ? "2px solid black" : "1px solid gray"}
                justifyContent="center"
                w="124px"
                p="5px"
                borderRadius="4px">
                <Text
                  fontSize="14px"
                  fontWeight={creditCardReceivement === "RECURRENT" ? "600" : "400"}
                  color={creditCardReceivement === "RECURRENT" ? "black" : "newGray.400"}
                >Recorrente</Text>
              </Flex>
            </Flex>
            {creditCardReceivement === "EARLY" && (
              <>
                <Text fontSize="12px" color="newGray.400">
                  O recebimento antecipado acontece apenas para o pagamento em
                </Text>
                <Text fontSize="12px" color="newGray.400" fontWeight="600">
                  cartão de crédito.
                </Text>
              </>
            )}
          </Flex>
        </Flex>
      </RadioGroup>
      {creditCardReceivement === "EARLY" && (
        <>
          <RadioGroup onChange={setCreditCardTax} value={creditCardTax}>
            <Flex flexDir="column" >
              <Flex align="center" gap=".3rem">
                <Text lineHeight="1.5rem" color="#000000" fontSize="12px">
                  Taxa de Antecipação das Parcelas
                </Text>
                <Tooltip
                  placement="top"
                  label="
                  Você pode optar por repassar as taxas de adiantamento
                  ao seu cliente na forma de juros de parcelameno ou pagar
                  você, mantendo o pagamento com valor fixo independente
                  de parcelas."
                >
                  <span>
                    <Icon w=".8rem" color="gray.400" as={AiOutlineInfoCircle} />
                  </span>
                </Tooltip>
              </Flex>
            </Flex>

            <Flex
              flexDir="row"
              maxW="332px" w="332px"
              gap="10px">
              <Flex
                onClick={() => setCreditCardTax("EARLY_BUYER")}
                cursor="pointer"
                border={creditCardTax === "EARLY_BUYER" ? "2px solid black" : "1px solid gray"}
                justifyContent="center"
                p="5px"
                w="124px"

                borderRadius="4px">
                <Text
                  fontSize="14px"
                  fontWeight={creditCardTax === "EARLY_BUYER" ? "600" : "400"}
                  color={creditCardTax === "EARLY_BUYER" ? "black" : "newGray.400"}
                >Cliente</Text>
              </Flex>


              <Flex

                onClick={() => setCreditCardTax("EARLY_SELLER")}
                cursor="pointer"
                border={creditCardTax === "EARLY_SELLER" ? "2px solid black" : "1px solid gray"}
                justifyContent="center"
                p="5px"
                w="124px"

                borderRadius="4px">
                <Text
                  fontSize="14px"
                  fontWeight={creditCardTax === "EARLY_SELLER" ? "600" : "400"}
                  color={creditCardTax === "EARLY_SELLER" ? "black" : "newGray.400"}
                >Você</Text>
              </Flex>
            </Flex>
            <Text
              maxW="332px" w="332px"
              lineHeight="1.5rem" fontSize="12px" color="newGray.400">
              {creditCardTax === "EARLY_BUYER" ?
                "Seu cliente assume as taxas e as taxas de processamento continuam sendo pagas por você" :
                "Você assume as taxas"
              }
            </Text>
          </RadioGroup>

          {creditCardTax === "EARLY_SELLER" && taxForBrands && istaxV2 === "true" && (
            <Flex
              flexDir="column"
              border="1px solid"
              borderColor="newGray.100"
              borderRadius="4px"
              padding=".8rem"
              gap="35px"
            >

              <Text fontSize="14px" fontWeight="600" color="newGray.900">
                Veja como fica o parcelamento em cada bandeira:
              </Text>

              <Tabs variant="line" colorScheme="blackAlpha">
                <TabList>
                  {Object?.keys(taxForBrands?.installmentsToSeller)?.map((bandeira) => (
                    <Tab key={bandeira} fontSize="14px" fontWeight="500" textTransform="capitalize">
                      {bandeira === 'default' ? 'Outras' : bandeira}
                    </Tab>
                  ))}
                </TabList>

                <TabPanels>
                  {Object?.entries(taxForBrands?.installmentsToSeller)?.map(([bandeira, dados]: [string, any[]]) => {
                    const tableData = dados.slice(0, installments)?.map((item) => ({
                      installments: item?.installments,
                      installmentValue: Number(finalValue) / item?.installments,
                      tax: item?.tax,
                      receivedAmount: item?.receivedAmount,
                    }));

                    const columns: ColumnDefinition<any>[] = [
                      {
                        key: "clientePaga",
                        title: "Cliente paga",
                        dataIndex: "installments",
                        align: "center",
                        render: (installments: number, record: any) => (
                          <Text fontSize="12px" textAlign="center" fontWeight="500">
                            {installments}x {"  "}
                            {record.installmentValue?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </Text>
                        ),
                      },
                      {
                        key: "taxa",
                        title: "Taxa",
                        dataIndex: "tax",
                        align: "center",
                        render: (tax: number) => (
                          <Text fontSize="12px" textAlign="center" maxW="100px">
                            {tax?.toFixed(2)?.replace('.', ',')} %
                          </Text>
                        ),
                      },
                      {
                        key: "total",
                        title: "Total",
                        dataIndex: "receivedAmount",
                        align: "center",
                        render: (receivedAmount: number) => (
                          <Text fontSize="12px" textAlign="center">
                            R$ {receivedAmount?.toFixed(2)?.replace('.', ',')}
                          </Text>
                        ),
                      },
                      {
                        key: "voceRecebe",
                        title: "Você recebe",
                        dataIndex: "receivedAmount",
                        align: "center",
                        render: (receivedAmount: number) => (
                          <Text fontSize="12px" textAlign="center">
                            {receivedAmount?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </Text>
                        ),
                      },
                    ];

                    return (
                      <TabPanel key={bandeira} px={0} py={4}>
                        <DynamicTable
                          data={tableData || []}
                          columns={columns}
                          rowKey={(record) => `${bandeira}-${record.installments}`}
                          locale={{ emptyText: "Sem dados" }}
                        />
                      </TabPanel>
                    );
                  })}
                </TabPanels>
              </Tabs>
            </Flex>
          )}

          {creditCardTax === "EARLY_SELLER" && istaxV2 === "false" && (
            <Flex flexDir="column"
              gap=".5rem">
              {(() => {
                const numericValue = previewData?.arrayInstallments?.installmentsToBuyer?.[0]?.installmentAmount ?? 0
                 
                
                const tableData = previewData?.arrayInstallments?.installmentsToSeller?.map((item: any) => ({
                  installments: item?.installments,
                  installmentAmount: numericValue / Number(item?.installments) ?? 0,
                  totalAmount:numericValue,
                  discount: item?.discount ?? 0,
                  receivedAmount: item?.receivedAmount ?? 0,
                })) || [];

                const columns: ColumnDefinition<any>[] = [
                  {
                    key: "clientePaga",
                    title: "Cliente paga",
                    dataIndex: "installments",
                    align: "center",
                    render: (installments: number) => (
                      <Text fontSize="12px" textAlign="center" fontWeight="500">
                        {installments}x
                      </Text>
                    ),
                  },
                  {
                    key: "parcela",
                    title: "Parcela",
                    dataIndex: "installmentAmount",
                    align: "center",
                    render: (installmentAmount: number) => (
                      <Text fontSize="12px" textAlign="center">
                        {formatCurrency({ value: installmentAmount })}
                      </Text>
                    ),
                  },
                  {
                    key: "total",
                    title: "Total",
                    dataIndex: "totalAmount",
                    align: "center",
                    render: (totalAmount: number) => (
                      <Text fontSize="12px" textAlign="center">
                        {formatCurrency({ value: totalAmount })}
                      </Text>
                    ),
                  },
                  {
                    key: "discount",
                    title: "Desconto",
                    dataIndex: "discount",
                    align: "center",
                    render: (discount: number) => (
                      <Text fontSize="12px" textAlign="center">
                        {discount > 0 ? `${discount} %` : "—"}
                      </Text>
                    ),
                  },
                  {
                    key: "voceRecebe",
                    title: "Você recebe",
                    dataIndex: "receivedAmount",
                    align: "center",
                    render: (receivedAmount: number) => (
                      <Text fontSize="12px" textAlign="center">
                        {formatCurrency({ value: receivedAmount })}
                      </Text>
                    ),
                  },
                ];

                return (
                  <DynamicTable
                    data={tableData}
                    columns={columns}
                    rowKey={(record) => `buyer-${record.installments}`}
                    locale={{ emptyText: "Sem dados" }}
                  />
                );
              })()}
            </Flex>
          )}

          {(creditCardTax === "EARLY_SELLER" && hasToogle === false && taxV2 === "false") && (
            <Flex gap=".5rem">

              <Flex
                w='85%'
                height="89px"
                border="1px solid #EDF1F7"
                borderRadius="2px"
                alignContent="center"
                alignItems="center"
                paddingLeft="16px"
                fontSize="14px"
                gap='3px'
              >
                <Text
                  fontWeight="600"
                  color="black"
                >
                  até
                </Text>
                <Text>
                  {previewData?.arrayInstallments?.installmentsToSeller.length}x
                </Text>
              </Flex>
              <Flex
                w='30%'
                height="89px"
                alignContent="center"
                alignItems="center"
                paddingLeft="16px"
              >
                <Img w="37px" h="52px" src="/images/commons/ArrowToRight.svg" />

              </Flex>
              <Flex
                w="85%"
                height="89px"
                border="1px solid #EDF1F7"
                borderRadius="2px"
                alignContent="center"
                alignItems="center"
                paddingLeft="16px"
                gap="3px"
              ><Text>
                  1x
                </Text>
                <Text
                  fontWeight="600"
                  color="black"
                >
                  {formatCurrency({ value: previewData?.arrayInstallments?.installmentsToSeller.at(-1)?.receivedAmount })}

                </Text>
              </Flex>
            </Flex>
          )}

          {creditCardTax === "EARLY_BUYER" && (
            < Flex id="card" flexDirection="column">
              {hasMixInstallments === "true" && (
                <Flex
                  flexDir="column"
                  border="1px solid"
                  borderColor="newGray.100"
                  borderRadius="4px"
                  padding=".8rem"
                  gap="35px"
                >
                  <Text
                    fontSize=".9rem"
                    color="black"
                    fontWeight="400"
                  >
                    Valor base apresentado para o cliente: {" "}
                    <b>
                      R$ {previewData?.value}
                    </b>
                    <Text
                      fontSize=".9rem"
                      color="newGray.400"
                      fontWeight="400"
                    >
                      Você assume as taxas em até <b>
                        {previewData?.creditcard?.installments}x
                      </b>
                    </Text>
                  </Text>


                  <Flex
                    flexDir="column"
                    gap=".5rem"
                  >
                    <Flex
                      w="100%"
                      justifyContent="space-between"

                    >
                      <Text
                        w="120%"
                      >
                        Cliente paga
                      </Text>
                      <Text
                        w="80%"

                      >
                        Você recebe
                      </Text>
                    </Flex>




                    <Flex gap=".5rem">

                      <Flex
                        w='80%'
                        height="89px"
                        border="1px solid #EDF1F7"
                        borderRadius="2px"
                        alignContent="center"
                        alignItems="center"
                        paddingLeft="16px"
                        fontSize="14px"
                        gap='3px'
                      >
                        <Text
                          fontWeight="600"
                          color="black"
                        >
                          até
                        </Text>
                        <Text>
                          21 x
                        </Text>
                      </Flex>
                      <Flex
                        w='30%'
                        height="89px"
                        alignContent="center"
                        alignItems="center"
                        paddingLeft="16px"
                      >
                        <Img w="37px" h="52px" src="/images/commons/ArrowToRight.svg" />

                      </Flex>
                      <Flex
                        w="85%"
                        height="89px"
                        border="1px solid #EDF1F7"
                        borderRadius="2px"
                        alignContent="center"
                        alignItems="center"
                        paddingLeft="16px"
                        gap="3px"
                      ><Text>
                          1 x
                        </Text>

                        <Text
                          fontWeight="600"
                          color="black"
                        >
                          {formatCurrency({ value: previewData?.arrayInstallments?.installmentsToSeller?.at(-1).receivedAmount })}


                        </Text>
                      </Flex>
                    </Flex>

                  </Flex>
                </Flex>
              )}

              {(previewData?.arrayInstallments && previewData?.value) && hasMixInstallments === "false" && (
                <Flex
                  flexDir="column"
                  border="1px solid"
                  borderColor="newGray.100"
                  borderRadius="4px"
                  padding=".8rem"
                  gap="35px"
                >

                  <Text
                    fontSize=".9rem"
                    color="newGray.900"
                    fontWeight="600"
                  >
                    Valor base apresentado para o cliente: <b>
                      {formatCurrency({ value: previewData?.arrayInstallments?.installmentsToBuyer?.at(0)?.totalValue })}
                    </b>
                    {previewData?.creditCardTax === "EARLY_SELLER" && hasToogle === true && (
                      <Text
                        fontSize=".9rem"
                        color="newGray.400"
                        fontWeight="400"
                      >
                        Parcelamento máximo selecionado: <b>
                          {previewData?.arrayInstallments?.installmentsToSeller.length}x
                        </b>
                      </Text>)
                    }
                  </Text>


                  <Flex
                    flexDir="column"
                    gap=".5rem"
                  >

                    {creditCardTax === "EARLY_BUYER" && (
                      <Flex flexDir="column"
                        gap=".5rem">
                        {(() => {
                          const tableData = previewData?.arrayInstallments?.installmentsToBuyer?.map((item: any) => ({
                            installments: item?.installments,
                            installmentAmount: item?.installmentAmount ?? 0,
                            totalAmount: item?.totalAmount ?? 0,
                            discount: item?.discount ?? 0,
                            receivedAmount: previewData?.arrayInstallments?.installmentsToSeller?.[0]?.receivedAmount ?? 0,
                          })) || [];

                          const columns: ColumnDefinition<any>[] = [
                            {
                              key: "clientePaga",
                              title: "Cliente paga",
                              dataIndex: "installments",
                              align: "center",
                              render: (installments: number) => (
                                <Text fontSize="12px" textAlign="center" fontWeight="500">
                                  {installments}x
                                </Text>
                              ),
                            },
                            {
                              key: "parcela",
                              title: "Parcela",
                              dataIndex: "installmentAmount",
                              align: "center",
                              render: (installmentAmount: number) => (
                                <Text fontSize="12px" textAlign="center">
                                  {formatCurrency({ value: installmentAmount })}
                                </Text>
                              ),
                            },
                            {
                              key: "total",
                              title: "Total",
                              dataIndex: "totalAmount",
                              align: "center",
                              render: (totalAmount: number) => (
                                <Text fontSize="12px" textAlign="center">
                                  {formatCurrency({ value: totalAmount })}
                                </Text>
                              ),
                            },
                            {
                              key: "desconto",
                              title: "Desconto",
                              dataIndex: "discount",
                              align: "center",
                              render: (discount: number) => (
                                <Text fontSize="12px" textAlign="center" color={discount > 0 ? "#0AA17C" : "newGray.400"}>
                                  {discount > 0 ? `${discount} %` : "—"}
                                </Text>
                              ),
                            },
                            {
                              key: "voceRecebe",
                              title: "Você recebe",
                              dataIndex: "receivedAmount",
                              align: "center",
                              render: (receivedAmount: number) => (
                                <Text fontSize="12px" textAlign="center">
                                  {formatCurrency({ value: receivedAmount })}
                                </Text>
                              ),
                            },
                          ];

                          return (
                            <DynamicTable
                              data={tableData}
                              columns={columns}
                              rowKey={(record) => `buyer-${record.installments}`}
                              locale={{ emptyText: "Sem dados" }}
                            />
                          );
                        })()}
                      </Flex>
                    )}

                    {creditCardTax === "EARLY_BUYER" && hasToogle === false && (
                      <Flex flexDir="column"
                        gap=".5rem">

                        {previewData?.arrayInstallments?.installmentsToBuyer?.map((item, index) => (
                          <Flex justifyContent="space-between"
                            key={index}  >
                            <Flex gap=".5rem" w="130%" >
                              <Flex flexDirection="column">
                                <Flex gap="10px"  >
                                  <Text
                                    fontSize=".9rem"
                                    color="newGray.400"
                                    fontWeight="400"
                                    aria-label={`Amount paind in ${index + 1} times`}
                                  >
                                    {item?.installments}x
                                  </Text>
                                  <Text
                                    fontSize=".9rem"
                                    color="black"
                                    fontWeight="400"
                                    aria-label={`Amount paind in ${index + 1} times`}
                                  >
                                    {formatCurrency({ value: previewData?.arrayInstallments.installmentsToBuyer?.at(0)?.installmentAmount / (index + 1) })}
                                  </Text>
                                </Flex>
                                <Text
                                  fontSize=".9rem"
                                  color="newGray.400"
                                  fontWeight="400"
                                  aria-label={`Amount paind in ${index + 1} times`}
                                >
                                </Text>

                              </Flex>
                              {item?.discount >= 0 && (
                                <Text
                                  fontSize=".9rem"
                                  color="#0AA17C"
                                  fontWeight="400"
                                  aria-label={`total discount`}
                                >
                                  {item?.discount}% de desconto
                                </Text>
                              )}
                            </Flex>

                            <Flex w="70%" gap="10px" >
                              <Text
                                fontSize=".9rem"
                                color="newGray.400"
                                fontWeight="400"
                                aria-label={`Amount paind in ${index + 1} times`}
                              >
                                1x
                              </Text>
                              <Text
                                fontSize=".9rem"
                                color="black"
                                fontWeight="400"
                                aria-label={`Amount paind in ${index + 1} times`}
                              >
                                {formatCurrency({ value: item?.receivedAmount })}

                              </Text>
                            </Flex>
                          </Flex>
                        ))}
                      </Flex>
                    )}
                 
                  </Flex>
                </Flex>
              )}

            </Flex>
          )}

          {
            isPunctualCharge && showAlert === true && (
              <Alert
                title="A fim de otimizar suas taxas, selecione um parcelamento máximo que acredita que seu cliente irá usar"
                variant="warning"
                onClose={() => setShowAlert(false)}
              />

            )
          }
        </>
      )}
    </>

  );
}
